import type { PricingMode } from "@/gql/graphql";

/**
 * Format price in cents to display format
 */
export function formatPrice(priceInCents: number, currency: string): string {
  const price = priceInCents / 100;

  // Determine locale based on currency for better formatting
  let locale = "en-US";
  if (currency === "EUR") {
    locale = "de-DE";
  } else if (currency === "VND") {
    locale = "vi-VN";
  } else if (currency === "JPY") {
    locale = "ja-JP";
  } else if (currency === "PLN") {
    locale = "pl-PL";
  } else if (currency === "CZK") {
    locale = "cs-CZ";
  }

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(price);
}

/**
 * Get pricing mode display text
 * @param mode - The pricing mode
 * @param t - Optional translation function. If not provided, returns English text
 */
export function getPricingModeLabel(mode: PricingMode, t?: (key: string) => string): string {
  if (t) {
    switch (mode) {
      case "NO_PRICE":
        return t("dialog.pricing.noPrice.title");
      case "SINGLE_PRICE":
        return t("dialog.pricing.fixedPrice.title");
      case "MATRIX_PRICE":
        return t("dialog.pricing.configurablePrice.title");
      default:
        return "Unknown";
    }
  }

  // Fallback to English if no translation function provided
  switch (mode) {
    case "NO_PRICE":
      return "No Price";
    case "SINGLE_PRICE":
      return "Fixed Price";
    case "MATRIX_PRICE":
      return "Configurable";
    default:
      return "Unknown";
  }
}

/**
 * Get pricing mode color class
 */
export function getPricingModeColor(mode: PricingMode): string {
  switch (mode) {
    case "NO_PRICE":
      return "bg-amber-100 text-amber-800";
    case "SINGLE_PRICE":
      return "bg-blue-100 text-blue-800";
    case "MATRIX_PRICE":
      return "bg-purple-100 text-purple-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

/**
 * Format price range display
 */
export function formatPriceRange(minPrice: number, maxPrice: number, currency = "USD"): string {
  if (minPrice === maxPrice) {
    return formatPrice(minPrice, currency);
  }
  return `${formatPrice(minPrice, currency)} - ${formatPrice(maxPrice, currency)}`;
}

/**
 * Calculate total price from base and options
 */
export function calculateTotalPrice(basePrice: number, optionsTotal: number): number {
  return basePrice + optionsTotal;
}

/**
 * Get price display text based on pricing mode
 * @param mode - The pricing mode
 * @param basePrice - Base price in cents
 * @param minPrice - Min price in cents
 * @param maxPrice - Max price in cents
 * @param currency - Currency code
 * @param t - Optional translation function. If not provided, returns English text
 */
export function getPriceDisplayText(
  mode: PricingMode,
  basePrice?: number,
  minPrice?: number,
  maxPrice?: number,
  currency = "USD",
  t?: (key: string) => string
): string {
  switch (mode) {
    case "NO_PRICE":
      return t ? t("pricing.noPriceSet") : "No price set";
    case "SINGLE_PRICE":
      return basePrice
        ? formatPrice(basePrice, currency)
        : t
          ? t("pricing.noPriceSet")
          : "No price set";
    case "MATRIX_PRICE":
      if (minPrice !== undefined && maxPrice !== undefined) {
        return formatPriceRange(minPrice, maxPrice, currency);
      }
      return t ? t("pricing.configurablePricing") : "Configurable pricing";
    default:
      return t ? t("pricing.priceNotAvailable") : "Price not available";
  }
}
